import { Power1, TweenMax } from 'gsap';
import { Container, Point } from 'pixi.js';
import { getGameSize, MapList, NODES_PER_MAP } from '../../Config/Levels';
import { IMapChests, MapChestsStates } from '../../Config/MapChests';
import Core, { GameOrientation } from '../../Core/Core';
import getLogger, { ILogger } from '../../Core/Logger';
import { getLevelIndex } from '../../State/reducers/level';
import { IMapData } from '../../State/reducers/player';
import { waitForMs } from '../../Utils/AsyncUtils';
import BezierPath, { IPathPoint } from '../../Utils/BezierPath';
import { getBoardJson } from '../../Utils/Utils';
import MapBackground from './MapBackground';
import { MapChest } from './MapChest';
import MapNode from './MapNode';
import { PlayerAvatarFrame } from './PlayerAvatarFrame';
import { MapActions, MapEmitter } from '../../Managers/Map/MapEmitter';
import { MapProgression } from '../../Managers/Map/MapProgression';
import { Platform } from '../../SDK/SDK';

const mapLogger: ILogger = getLogger('[AreaMap]:');

interface IMapAvatars {
    [key: string]: PlayerAvatarFrame;
}

export default class AreaMap extends Container {
    public mapBg: MapBackground;
    public mapWidth: number;
    public nodes: MapNode[];
    public chests: MapChest[] = []; // array of all the chests on the areamap
    private chestContainer: Container; // container containing all the chests on the areamap
    private pathPoints: Point[];
    public dragging: boolean = false;
    private playerAvatar: PlayerAvatarFrame;
    private avatars: IMapAvatars = {};
    private nodeAvatars: PlayerAvatarFrame[][] = [];
    private selectedNode: MapNode = null;

    constructor(mapId: string, levels: IMapData) {
        super();

        this.mapBg = new MapBackground();
        this.mapWidth = this.mapBg.width;

        const dataPoints: IPathPoint[] = Core.gameCache.jsons['map-path'].selection[0].pathPoints;
        const mapLevels: string[] = [];
        const objKey =
            Object.keys(Core.gameCache.jsons).find(key => key.includes(mapId + '-levels')) ||
            'machu';
        const json = Core.gameCache.jsons[objKey] || null;
        const numLevels = json ? json.length : 0;
        const startLevel = MapList.indexOf(mapId) * NODES_PER_MAP;

        for (let i = startLevel; i < startLevel + numLevels; i++) {
            const levelIndex = i + 1;
            if (
                levelIndex > MapList.indexOf(mapId) * NODES_PER_MAP &&
                levelIndex <= (MapList.indexOf(mapId) + 1) * NODES_PER_MAP
            ) {
                mapLevels.push(`level-${levelIndex}`);
            }
        }

        mapLogger.debug('levels for this map: ' + mapLevels);

        this.pathPoints = BezierPath.getPathPoints(dataPoints, NODES_PER_MAP);

        const nodeContainer = new Container();

        this.chestContainer = new Container();

        const { mapLastPlayedLevel, gainedStars } = MapProgression.current;
        this.nodes = mapLevels
            .map(key => key.replace('level-', ''))
            .sort((a, b) => parseInt(a, 10) - parseInt(b, 10))
            .map((_, index) => {
                const levelId = mapId + '-' + (index + 1).toString(10);
                const stars = levels[levelId] ? levels[levelId].stars : 0;
                const amt = gainedStars || 0;
                const gainStars = mapLastPlayedLevel - 1 === index ? amt : 0;
                const levelIndex = getLevelIndex(levelId);
                const levelConfig = getBoardJson(levelId).config;
                const { goal, winTime } = levelConfig;
                const node = new MapNode(
                    stars,
                    levelId,
                    levelIndex,
                    gainStars,
                    goal.type,
                    winTime !== 0
                );
                const nodeScale = Core.orientation === GameOrientation.PORTRAIT ? 0.7 : 0.5;
                node.scale.set(nodeScale);
                node.position.set(this.pathPoints[index].x, this.pathPoints[index].y);
                node.on('pointerdown', this.setCurrentSelectedNode.bind(this));
                node.on('pointerup', this.checkCurrentSelectedNode.bind(this));
                return node;
            });

        const placeNodes = this.nodes.slice(0).reverse();
        nodeContainer.addChild(placeNodes[0], ...placeNodes);
        this.nodes.forEach(() => {
            this.nodeAvatars.push([]);
        });

        this.addChild(this.mapBg);
        this.addChild(nodeContainer);
        this.addChild(this.chestContainer);

        // this.nodeCallback = cb;
        // this.chestCallback = cbChests;
    }

    public centerOn(nodeIndex: number) {
        const nodeY = this.nodes[nodeIndex].y;
        this.y =
            -this.height + getGameSize().gameHeight * 0.5 + (this.height - nodeY * this.scale.y);
        if (this.y > 0) {
            this.y = 0;
        }

        if (this.y < -this.height + getGameSize().gameHeight) {
            this.y = -this.height + getGameSize().gameHeight;
        }
    }

    public update() {}

    public addAvatar(url: string, isPlayer: boolean, currentNodeNumber: number) {
        if (!url) {
            return;
        }

        if (currentNodeNumber > this.nodes.length) {
            currentNodeNumber = this.nodes.length;
        }
        const currentNode = this.nodes[currentNodeNumber - 1];
        const avatar = new PlayerAvatarFrame(url, isPlayer);
        avatar.scale.set(currentNode.scale.x);
        avatar.x = currentNode.x - avatar.width;
        avatar.y = currentNode.y;

        if (isPlayer) {
            this.playerAvatar = avatar;
            avatar.x = currentNode.x + avatar.width;
            avatar.y = currentNode.y;
        }

        if (sgPlatform === Platform.Network) {
            avatar.visible = false;
        }

        if (isPlayer || this.nodeAvatars[currentNodeNumber - 1].length < 5) {
            this.nodeAvatars[currentNodeNumber - 1].unshift(avatar);
            this.avatars[url] = avatar;

            this.shiftAllAtNode(currentNodeNumber - 1, false);

            this.addChild(avatar);
        }
    }

    public setActiveNode(index: number) {
        if (index > this.nodes.length) {
            index = this.nodes.length;
        }
        this.nodes[index - 1].setActive();
    }

    public async highlightAvatar(id: string) {
        await this.avatars[id].showActive();
    }

    public async showNodeProgress(score: number, index: number) {
        await this.nodes[index - 1].showScore(score);
    }

    public async moveToNode(nodeIndex: number) {
        // if player is moved to node with other players move them over
        this.nodeAvatars[nodeIndex - 2].splice(
            this.nodeAvatars[nodeIndex - 2].indexOf(this.playerAvatar),
            1
        );
        // if player is moved from node with other players move them over
        this.nodeAvatars[nodeIndex - 1].unshift(this.playerAvatar);
        const avatar = this.playerAvatar;

        const node = this.nodes[nodeIndex - 1];

        TweenMax.to(avatar, 1, {
            x: node.x + avatar.width * 0.9,
            y: node.y,
            ease: Power1.easeOut,
            onComplete: () => {},
        });
        await waitForMs(1000);
        return;
    }

    public cleanUp() {
        this.nodes.forEach(n => {
            n.destroy();
        });
        this.nodes = [];
        Object.values(this.avatars).forEach(a => a.destroyAvatar());
        this.avatars = {};
        this.nodeAvatars = [];
        this.mapBg.cleanUp();
        this.destroy();
    }

    private shiftAllAtNode(index: number, animated: boolean = false) {
        // calc all positions from
        const allAtNode = this.nodeAvatars[index].slice(0).filter(a => {
            return a !== this.playerAvatar;
        });
        allAtNode.forEach((a, i) => {
            if (a !== this.playerAvatar) {
                const targetX = this.nodes[index].x - a.width * 0.7 - a.width * i * 0.25;
                if (animated) {
                    TweenMax.to(a, 0.2, { x: targetX });
                } else {
                    a.x = targetX;
                }
            }
        });
    }

    private setCurrentSelectedNode(e: PIXI.interaction.InteractionEvent) {
        this.selectedNode = e.currentTarget as MapNode;
    }

    private checkCurrentSelectedNode(e: PIXI.interaction.InteractionEvent) {
        if (e.currentTarget === this.selectedNode && !this.dragging) {
            this.handleNodeClick(this.selectedNode);
        }
        this.selectedNode = null;
    }

    private handleNodeClick(node: MapNode) {
        MapEmitter.instance.emit(MapActions.LEVEL_SELECT, node.nodeId);
    }

    /**
     * adds a chest to the area map
     * @param mapChest the map chest to add
     * @param unlockedChest string of the current state of the chest
     */
    public addChests(mapChest: IMapChests, unlockedChest: MapChestsStates) {
        const chest = new MapChest(mapChest, unlockedChest);
        // chest.scale.set(0.5);
        mapLogger.debug(['added chest: ', chest.chests.levelNum]);
        if (!mapChest.position) {
            if (this.chests.length === 0) {
                chest.x = this.mapBg.width * 0.755;
                chest.y = this.mapBg.height * 0.835;
            } else if (this.chests.length === 1) {
                chest.x = this.mapBg.width * 0.77;
                chest.y = this.mapBg.height * 0.325;
            } else if (this.chests.length === 2) {
                chest.x = this.mapBg.width * 0.41;
                chest.y = this.mapBg.height * 0.065;
            }
        }
        chest.sprite.on('pointerup', () => {
            this.handleChestClick(chest);
        });

        this.chests.push(chest);
        this.chestContainer.addChild(chest);
    }

    private handleChestClick(chest: MapChest) {
        MapEmitter.instance.emit(MapActions.CHEST_SELECT, chest);
    }
}
